import { z } from "zod";
import { apiClient } from "./api-client";
import { phraseListResponseSchema } from "./phrase-list.contract";
import {
  addPhraseResponseSchema,
  curatePhrasesRequestSchema,
  curatePhrasesResponseSchema,
  excelUploadResponseSchema,
  importPhrasesResponseSchema,
  initialDataImportsResponseSchema,
  removePhrasesResponseSchema,
  type ExcelFileInfo,
} from "./excel-import.contract";

export type { ExcelFileInfo } from "./excel-import.contract";

export const wordApi = {
  getFilesByLang: async (lang: string, page = 1, limit = 10) => {
    const { data } = await apiClient.get(`/words/files/lang/${lang}`, {
      params: { page, limit },
    });
    return phraseListResponseSchema.parse(data).data;
  },

  downloadFile: async (id: number) => {
    const response = await apiClient.get(`/words/${id}/download`, {
      responseType: "blob",
    });
    const disposition = response.headers["content-disposition"] as
      string | undefined;
    const filename =
      disposition?.split("filename=")[1]?.replace(/"/g, "") ||
      `excel-${id}.xlsx`;
    return { blob: response.data, filename };
  },

  updateFile: async (id: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await apiClient.put(`/words/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return data;
  },

  deleteFile: async (id: number) => {
    const { data } = await apiClient.delete(`/words/${id}`);
    return data;
  },

  getInitialDataFiles: async (): Promise<ExcelFileInfo[]> => {
    const response = await apiClient.get("/words/initial-data-files");
    return initialDataImportsResponseSchema.parse(response.data).data;
  },

  approveFile: async (id: number) => {
    const { data } = await apiClient.post(`/words/${id}/approve`);
    return data;
  },

  rejectFile: async (id: number, reason: string) => {
    const { data } = await apiClient.post(`/words/${id}/reject`, { reason });
    return data;
  },

  /** Sent by a super admin it goes live at once, otherwise it waits for approval. */
  uploadExcel: async (lang: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("lang", lang);
    const response = await apiClient.post("/words/upload-excel", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return excelUploadResponseSchema.parse(response.data);
  },

  addPhrase: async (
    id: number,
    phrase: { local: string; fr: string; source?: string; domain?: string },
  ) => {
    const response = await apiClient.post(`/words/${id}/phrases`, phrase);
    return addPhraseResponseSchema.parse(response.data);
  },

  /** Rows have no id: the backend removes the row matching both texts. */
  removePhrase: async (id: number, phrase: { local: string; fr: string }) => {
    const { data } = await apiClient.post(
      `/words/${id}/phrases/remove`,
      phrase,
    );
    return data as { message: string; fileId: number; rows: number };
  },

  removePhrases: async (
    id: number,
    phrases: Array<{ local: string; fr: string }>,
  ) => {
    const response = await apiClient.post(`/words/${id}/phrases/remove-batch`, {
      phrases,
    });
    return removePhrasesResponseSchema.parse(response.data);
  },

  importPhrases: async (id: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await apiClient.post(
      `/words/${id}/phrases/import`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return importPhrasesResponseSchema.parse(response.data);
  },

  curatePhrases: async (
    id: number,
    request: z.infer<typeof curatePhrasesRequestSchema>,
  ) => {
    const payload = curatePhrasesRequestSchema.parse(request);
    const response = await apiClient.put(
      `/words/${id}/phrases/curation`,
      payload,
    );
    return curatePhrasesResponseSchema.parse(response.data);
  },
  deleteAllEXcelFilesByLang: async (lang: string) => {
    const { data } = await apiClient.delete(`/words/delete-by-lang/${lang}`);
    return data;
  },

  updateTranslation: async (
    language: string,
    currentPhrase: string,
    newValue: string,
    updateType: "local" | "french",
  ) => {
    const { data } = await apiClient.put(
      `/words/${encodeURIComponent(currentPhrase)}/translation`,
      {
        language,
        newValue,
      },
      {
        params: { type: updateType },
      },
    );
    return data;
  },
};
